import {
  defaultFilters,
  initialTripsPanelState,
  initStateFilters,
  tripsPanelLocalStorageKey,
  tripsStoreLocalStorageKey,
} from './initial-trips-state';
import { getOldAndNewFilters } from './utils/filters-utils';
import { type TripsData, type TripsPanelState, type JobTypes, type Filters } from '../../types';
import localStorageService from '../../services/LocalStorageService';
import { MapLayer, type Settings, type OperationViewModel } from '../Map/types';
import type { GlobalContext, TripsContext } from './types';

export const initialTripsState: GlobalContext = {
  trips: {
    port: initStateFilters.port,
    endDate: initStateFilters.endDate,
    jobType: initStateFilters.jobType,
    startDate: initStateFilters.startDate,
    tripType: initStateFilters.tripType,
    vesselName: initStateFilters.vesselName,
    previousFilters: initStateFilters,
    tripsData: { isLoading: false, trips: [] },
    operations: [],
    tripsPanelState: initialTripsPanelState,
  },
  map: {
    settings: {
      layer: MapLayer.Default,
      showVesselTracks: true,
      showVesselNames: true,
      showQuiverPlot: true,
      showNauticalMap: true,
    },
  },
};

export type Action =
  | { type: ActionType.UPDATE_OPERATION; payload: string | null }
  | { type: ActionType.UPDATE_START_DATE; payload: string | null }
  | { type: ActionType.UPDATE_END_DATE; payload: string | null }
  | { type: ActionType.UPDATE_VESSEL_NAME; payload: string | null }
  | { type: ActionType.UPDATE_TRIP_TYPE; payload: string[] | null }
  | { type: ActionType.UPDATE_JOB_TYPE; payload: JobTypes | null }
  | { type: ActionType.RESET_FILTER }
  | { type: ActionType.SET_TRIPS; payload: TripsData }
  | { type: ActionType.SET_OPERATIONS; payload: OperationViewModel[] }
  | { type: ActionType.UPDATE_SETTINGS; payload: Settings }
  | { type: ActionType.SET_TRIPS_PANEL_STATE; payload: { tripsPanelState: TripsPanelState } };

export enum ActionType {
  UPDATE_OPERATION,
  UPDATE_START_DATE,
  UPDATE_END_DATE,
  UPDATE_VESSEL_NAME,
  UPDATE_TRIP_TYPE,
  UPDATE_JOB_TYPE,
  RESET_FILTER,
  SET_TRIPS,
  SET_OPERATIONS,
  UPDATE_SETTINGS,
  SET_TRIPS_PANEL_STATE,
}

export const globalReducer = (state: GlobalContext, action: Action): GlobalContext => {
  switch (action.type) {
    case ActionType.UPDATE_OPERATION:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { port: action.payload }),
        },
      };
    case ActionType.UPDATE_START_DATE:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { startDate: action.payload }),
        },
      };
    case ActionType.UPDATE_END_DATE:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { endDate: action.payload }),
        },
      };
    case ActionType.UPDATE_VESSEL_NAME:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { vesselName: action.payload }),
        },
      };
    case ActionType.UPDATE_TRIP_TYPE:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { tripType: action.payload }),
        },
      };
    case ActionType.UPDATE_JOB_TYPE:
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { jobType: action.payload }),
        },
      };
    case ActionType.RESET_FILTER: {
      return {
        ...state,
        trips: {
          ...state.trips,
          ...updateFilters(state.trips, { ...defaultFilters, port: state.trips.port }),
        },
      };
    }
    case ActionType.SET_TRIPS:
      return {
        ...state,
        trips: {
          ...state.trips,
          tripsData: action.payload,
        },
      };
    case ActionType.SET_OPERATIONS:
      return {
        ...state,
        trips: {
          ...state.trips,
          operations: action.payload,
        },
      };
    case ActionType.UPDATE_SETTINGS:
      return {
        ...state,
        map: {
          ...state.map,
          settings: action.payload,
        },
      };
    case ActionType.SET_TRIPS_PANEL_STATE:
      localStorageService.setItem(tripsPanelLocalStorageKey, action.payload.tripsPanelState);

      return {
        ...state,
        trips: {
          ...state.trips,
          tripsPanelState: action.payload.tripsPanelState,
        },
      };
    default:
      return state;
  }
};

const updateFilters = (
  context: TripsContext,
  update: Partial<Filters>
): Omit<TripsContext, 'tripsData' | 'operations' | 'tripsPanelState'> => {
  const filters = getOldAndNewFilters(context, update);
  localStorageService.setItem(tripsStoreLocalStorageKey, filters.new);
  return {
    ...filters.new,
    previousFilters: filters.old,
  };
};
